import { createSlice, current, PayloadAction } from '@reduxjs/toolkit'
import { setSuccess, unSet, setLoading } from 'api/remoteData'
import { isBefore, isToday, startOfDay } from 'date-fns'
import {
  getNotifications,
  getUnReadNotificationCount,
  readAllNotifications,
  updateNotificationStatus,
} from './../services/notification'
import {
  GetNotificationResponse,
  GetNotificationCountResponse,
  Notification as NotificationResponse,
  LeadNameChangedData,
  LeadProcessStageRemovedData,
  LeadProcessStageAddedData,
  LeadOwnerChangedData,
  QuotationChangedStatus,
} from 'services/types/notification'

import { NotificationProps } from './types/notification'
import { Notification } from 'types/global'
import { SocketEvents } from '../socket/events'
import { formatDatetime } from 'utils/datetime'

const initialState: NotificationProps = {
  notificationCenter: unSet(),
  notificationCount: unSet(),
  paginatedData: [],
}

const { reducer, actions } = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    clear: () => initialState,
    addEndofList: (state) => {
      const currentNotifications = current(state)
        .paginatedData.filter((item) => item.itemType === 'notification')
        .map((item: Notification) => ({
          createTs: item.createTs,
          data: item?.data,
          isRead: item.isRead,
          leadId: item.leadId,
          recordId: item.recordId,
          notificationId: item.notificationId,
          notificationType: item.notificationType,
          itemType: 'notification',
        })) as Notification[]
      const notiEnd = new Object({}) as Notification
      notiEnd.itemType = 'endofnotification'

      return { ...state, paginatedData: [...populateDayBreak([...currentNotifications]), notiEnd] }
    },
    removeEndofList: (state) => {
      const currentNotifications = current(state)
        .paginatedData.filter((item) => item.itemType === 'notification')
        .map((item: Notification) => ({
          createTs: item.createTs,
          data: item?.data,
          isRead: item.isRead,
          leadId: item.leadId,
          recordId: item.recordId,
          notificationId: item.notificationId,
          notificationType: item.notificationType,
          itemType: 'notification',
        })) as Notification[]

      return { ...state, paginatedData: populateDayBreak([...currentNotifications]) }
    },
    appendNotification: (
      state,
      {
        payload: { data, notificationType },
      }: PayloadAction<{
        notificationType: SocketEvents
        data:
          | LeadNameChangedData
          | LeadProcessStageAddedData
          | LeadProcessStageRemovedData
          | LeadOwnerChangedData
          | QuotationChangedStatus
      }>,
    ) => {
      const currentNotifications = current(state)
        .paginatedData.filter((item) => item.itemType === 'notification')
        .map((item: Notification) => ({
          createTs: item.createTs,
          data: item?.data,
          isRead: item.isRead,
          leadId: item.leadId,
          recordId: item.recordId,
          notificationId: item.notificationId,
          notificationType: item.notificationType,
          itemType: 'notification',
        })) as Notification[]

      currentNotifications.unshift({
        createTs: new Date().toISOString(),
        data: mapNotificationData(data, notificationType),
        isRead: false,
        leadId: '',
        recordId: currentNotifications.length > 0 ? currentNotifications[0].recordId + 1 : 0,
        notificationId: data.notification_id || '',
        notificationType: notificationType,
        itemType: 'notification',
      })

      return { ...state, paginatedData: populateDayBreak([...currentNotifications]) }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNotifications.pending, (state, { meta }) => ({
      ...state,
      notificationCenter: setLoading(meta.requestId),
      paginatedData: populateDayBreak(
        [...current(state).paginatedData]
          .filter((item) => item.itemType === 'notification')
          .filter((item, index, self) => index === self.findIndex((t) => t.recordId === item.recordId)),
      ),
    }))

    builder.addCase(getNotifications.fulfilled, (state, { meta, payload }) => {
      if (payload.status === 'FAIL') {
        return { ...state, notificationCenter: payload }
      }

      return {
        ...state,
        notificationCenter: setSuccess(mapNotifications(payload.data), meta.requestId),
        notificationCount: setSuccess(mapNotificationCount(payload.data)),
        paginatedData: populateDayBreak(
          [...current(state).paginatedData, ...mapNotifications(payload.data)]
            .filter((item) => item.itemType === 'notification')
            .filter((item, index, self) => index === self.findIndex((t) => t.recordId === item.recordId)),
        ),
      }
    })

    builder.addCase(getUnReadNotificationCount.fulfilled, (state, { payload }) => {
      if (payload.status === 'FAIL') {
        return { ...state, notificationCount: payload }
      }

      return {
        ...state,
        notificationCount: setSuccess(mapNotificationCount(payload.data)),
        paginatedData: populateDayBreak(
          [...current(state).paginatedData]
            .filter((item) => item.itemType === 'notification')
            .filter((item, index, self) => index === self.findIndex((t) => t.recordId === item.recordId)),
        ),
      }
    })

    builder.addCase(readAllNotifications.fulfilled, (state, { payload }) => {
      if (payload.status === 'FAIL') {
        return { ...state }
      }
      const currentNotifications = current(state)
        .paginatedData.filter((item) => item.itemType === 'notification')
        .map((item: Notification) => ({
          createTs: item.createTs,
          data: item?.data,
          isRead: true,
          leadId: item.leadId,
          recordId: item.recordId,
          notificationId: item.notificationId,
          notificationType: item.notificationType,
          itemType: 'notification',
        })) as Notification[]

      return {
        ...state,
        paginatedData: populateDayBreak(
          [...currentNotifications].filter(
            (item, index, self) => index === self.findIndex((t) => t.recordId === item.recordId),
          ),
        ),
      }
    })

    builder.addCase(updateNotificationStatus.fulfilled, (state, { payload, meta }) => {
      if (payload.status === 'FAIL') {
        return { ...state }
      }
      const currentNotifications = current(state)
        .paginatedData.filter((item) => item.itemType === 'notification')
        .map((item: Notification) => ({
          createTs: item.createTs,
          data: item?.data,
          isRead: item.isRead,
          leadId: item.leadId,
          recordId: item.recordId,
          notificationId: item.notificationId,
          notificationType: item.notificationType,
          itemType: 'notification',
        })) as Notification[]

      const mutatedIndex = currentNotifications.findIndex((item) => item.notificationId === meta?.arg?.notification_id)

      if (mutatedIndex !== -1) {
        const mutatedItem = currentNotifications[mutatedIndex]
        mutatedItem.isRead = !mutatedItem.isRead
        currentNotifications[mutatedIndex] = mutatedItem
      }

      return {
        ...state,
        paginatedData: populateDayBreak(
          [...currentNotifications].filter(
            (item, index, self) => index === self.findIndex((t) => t.recordId === item.recordId),
          ),
        ),
      }
    })
  },
})

const mapNotificationCount = (data: GetNotificationCountResponse) => ({ totalUnread: data.total_unread })

const populateDayBreak = (data: Notification[]) => {
  const result: Notification[] = []
  let isNotificationToday = false
  let isNotificationOlder = false

  data.forEach((i) => {
    const formattedCreateTs = formatDatetime(i.createTs, 'MM/dd/yyyy')
    const noti = Object({}) as Notification
    if (!isNotificationToday && Boolean(formattedCreateTs && isToday(new Date(formattedCreateTs)))) {
      isNotificationToday = true
      noti.itemType = 'daybreaktoday'
      result.push(noti)
    } else if (
      !isNotificationOlder &&
      Boolean(formattedCreateTs && isBefore(new Date(formattedCreateTs), startOfDay(new Date())))
    ) {
      isNotificationOlder = true
      noti.itemType = 'daybreakolder'
      result.push(noti)
    }

    result.push({ ...i })
  })
  return result
}

export const mapNotifications: (data: GetNotificationResponse) => Notification[] = (data) =>
  data.notifications.map((item) => ({
    createTs: item.create_ts,
    data: mapNotificationData(item.data.data, item.notification_type),
    isRead: item.status === 'read',
    leadId: item.lead_id,
    recordId: item.record_id,
    notificationId: item.notification_id,
    notificationType: item.notification_type,
    status: item.status,
    itemType: 'notification',
  }))

const mapNotificationData: (
  data: NotificationResponse['data']['data'],
  type: NotificationResponse['notification_type'],
) => Notification['data'] = (data, type) => {
  switch (type) {
    case SocketEvents.LEAD_OWNER_UPDATE:
      return data as LeadOwnerChangedData
    case SocketEvents.QUOTATION_STATUS_UPDATE:
      return data as QuotationChangedStatus
    case SocketEvents.LEAD_NAME_UPDATE:
    case SocketEvents.LEAD_STATUS_UPDATE:
    case SocketEvents.LEAD_PROCESS_UPDATE:
      const leadNameUpdateMeta = data as LeadNameChangedData

      return {
        leadName: leadNameUpdateMeta?.lead_name,
        newValue: leadNameUpdateMeta?.new_value,
        oldValue: leadNameUpdateMeta?.old_value,
      }
    case SocketEvents.WORKFLOW_PROCESS_STAGE_ADD:
    case SocketEvents.WORKFLOW_PROCESS_STAGE_DELETE:
      const workflowProcessStageMeta = data as LeadProcessStageRemovedData

      return {
        affectedLeadsCount: workflowProcessStageMeta?.affected_leads_count,
        leadProcessName: workflowProcessStageMeta?.lead_process_name,
        leadStatusName: workflowProcessStageMeta?.lead_status_name,
        newStatusName: workflowProcessStageMeta?.new_status_name,
      }
    default:
  }
}

export default reducer
export { actions }
